import { Box } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import ChangePwdIcon from '@assets/images/ChangePassword.svg';
import { Formik, ErrorMessage } from 'formik';
import { useFacilityChangePassword } from '../../API/hooks/loginHook';
import { useState, useRef } from 'react';
import type { ErrorType } from '../../shared/lib/utils/errorType';
import type {
  ChangePassword as ChangePasswordType,
  FacilityResponce,
} from '../../API/services/loginServices';
import * as Yup from 'yup';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import { getUserFromStorage } from '../components/storageUtility';

const initialValues: ChangePasswordType = {
  userId: 0,
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};
const validationData =
  'Password must be between 8 and 50 characters long and include at least one number, one lowercase letter, one uppercase letter, and one special character.';
const passwordValidationSchema = Yup.string()
  .min(8, validationData)
  .max(50, validationData)
  .matches(/\d/, validationData)
  .matches(/[a-z]/, validationData)
  .matches(/[A-Z]/, validationData)
  .matches(/[^a-zA-Z0-9]/, validationData)
  .required('New password is required');
const validationSchema = Yup.object({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: passwordValidationSchema,
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match!'),
});
const ChangePassword = ({
  closePopup,
  successMessage,
}: {
  closePopup: () => void;
  successMessage: (message: string) => void;
}) => {
  const MessageRef = useRef('');
  const user = getUserFromStorage();
  const { mutate: mutateChangePassword } = useFacilityChangePassword();
  const [openToast, setOpenToast] = useState(false);

  const handleChangePassword = (request: ChangePasswordType) => {
    mutateChangePassword(
      {
        userId: user?.userId ?? 0,
        currentPassword: request.currentPassword,
        newPassword: request.newPassword,
        confirmPassword: request.confirmPassword,
      },
      {
        onSuccess: (data: FacilityResponce) => {
          if (data.code === 200) {
            successMessage(data.message);
            closePopup();
          }
        },

        onError: (error: Error | ErrorType) => {
          MessageRef.current = error.message;
          setOpenToast(true);
          setTimeout(() => {
            setOpenToast(false);
            MessageRef.current = '';
          }, 8000);
        },
      }
    );
  };
  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };
  return (
    <Box className="dialog-width">
      {openToast && (
        <CustomSnackbar
          id="login-toast"
          successOrError={'error'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={MessageRef.current}
        />
      )}
      <Box
        style={{
          position: 'absolute',
          top: '-9999px',
          height: 0,
          overflow: 'hidden',
        }}
      >
        <input
          type="text"
          id="fake-input"
          name="fake-input"
          autoComplete="username"
        />
      </Box>
      <Formik
        initialValues={initialValues}
        validateOnMount={true}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleChangePassword(values);
        }}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <Grid2 container spacing={2} justifyContent="center">
            <CustomDialogContent className="pt-xs">
              <Grid2 container className="pb-16">
                <Box>
                  <Grid2 size={{ xs: 12 }}>
                    <ChangePwdIcon />
                  </Grid2>
                </Box>
              </Grid2>
              <Grid2 container>
                <Box className="pb-6">
                  <Grid2 size={{ xs: 12 }}>
                    <CustomTypography id="title-popup" variant="subtitle1">
                      Change Password
                    </CustomTypography>
                  </Grid2>
                </Box>
                <Box>
                  <Grid2 size={{ xs: 12 }}>
                    <CustomTypography
                      id="demo-h2"
                      variant="body2"
                      color="textSecondary"
                    >
                      Please enter a new password that is between 8 and 50
                      characters long and includes at least one number, one
                      lowercase letter, one uppercase letter, and one special
                      character.
                    </CustomTypography>
                  </Grid2>
                </Box>
              </Grid2>

              <Grid2 container className="pt-24">
                <Grid2 size={12} className="mb-16">
                  <CustomTypography
                    id="currentpwd"
                    variant="subtitle2"
                    className="pb-8"
                  >
                    Current Password
                  </CustomTypography>
                  <CustomTextField
                    className="mb-28"
                    id="txt-current-Password"
                    name="current-password-iiodnn"
                    variant="outlined"
                    placeholder="Current Password"
                    value={values.currentPassword}
                    onChange={(e) =>
                      setFieldValue('currentPassword', e.target.value)
                    }
                    autoComplete="new-password"
                    size="small"
                    type="password"
                    isPassword={true}
                  />
                  <CustomTypography
                    className="pt-8 float-left"
                    variant="body3"
                    id="error-currentpwd"
                    color="error"
                  >
                    <ErrorMessage name="currentPassword" component="div" />
                  </CustomTypography>
                </Grid2>
                <Grid2 size={{ xs: 12 }} className="mb-16">
                  <CustomTypography
                    id="newpwd"
                    variant="subtitle2"
                    className="pb-8"
                  >
                    New Password
                  </CustomTypography>
                  <CustomTextField
                    id="newPassword"
                    name="new-password-uienfd"
                    variant="outlined"
                    placeholder="New Password"
                    autoComplete="new-password"
                    value={values.newPassword}
                    onChange={(e) =>
                      setFieldValue('newPassword', e.target.value)
                    }
                    size="small"
                    type="password"
                    isPassword={true}
                  />
                  <CustomTypography
                    className="pt-8 float-left"
                    variant="body3"
                    id="error-newpwd"
                    color="error"
                  >
                    <ErrorMessage name="newPassword" component="div" />
                  </CustomTypography>
                </Grid2>
                <Grid2 size={12} className="pb-16">
                  <CustomTypography
                    id="confirmpwd"
                    variant="subtitle2"
                    className="pb-8"
                  >
                    Confirm Password
                  </CustomTypography>
                  <CustomTextField
                    id="confirmPassword"
                    variant="outlined"
                    placeholder="Confirm Password"
                    autoComplete="new-password"
                    value={values.confirmPassword}
                    onChange={(e) =>
                      setFieldValue('confirmPassword', e.target.value)
                    }
                    size="small"
                    type="password"
                    isPassword={true}
                  />
                  <CustomTypography
                    className="pt-8 float-left"
                    variant="body3"
                    id="error-confirmpwd"
                    color="error"
                  >
                    <ErrorMessage name="confirmPassword" component="div" />
                  </CustomTypography>
                </Grid2>
                <Grid2 size={12}>
                  <Box className="flex float-right">
                    <Box className="mr-8">
                      <CustomButton
                        id="btnClickMe2"
                        onClick={() => closePopup()}
                        color="error"
                        variant="outlined"
                        size="medium"
                      >
                        Cancel
                      </CustomButton>
                    </Box>
                    <Box>
                      <CustomButton
                        id="btnAdd"
                        onClick={() => handleSubmit()}
                        color="primary"
                        size="small"
                      >
                        Submit
                      </CustomButton>
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </CustomDialogContent>
          </Grid2>
        )}
      </Formik>
    </Box>
  );
};

export default ChangePassword;
